// ExpandingField.tsx — auto-growing textarea with label row (Clear chip,
// Required badge, optional badge slot). Port of NPFieldExpand from
// docs/design/design_handoff_add_new_product/newproduct-data.jsx.
import React, { useEffect, useRef, useState } from "react";
import { RequiredBadge, ClearChip } from "./atoms";

export function ExpandingField({
    label,
    value,
    onChange,
    onClear,
    onBlur,
    placeholder,
    rows = 1,
    required,
    badge,
    maxH = 520,
    maxLength,
    error
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    onClear: () => void;
    /** Called with the current value when focus leaves the field. */
    onBlur?: (value: string) => void;
    placeholder?: string;
    rows?: number;
    required?: boolean;
    badge?: React.ReactNode;
    maxH?: number;
    maxLength?: number;
    error?: string;
}) {
    const [focus, setFocus] = useState(false);
    const ref = useRef<HTMLTextAreaElement>(null);
    const suppressNextBlurRef = useRef(false);
    const base = rows * 21 + 20;

    useEffect(() => {
        if (!onBlur) return;

        // Observe the pointer boundary before the browser changes focus. Some
        // resumed-draft interactions do not produce a dependable textarea
        // blur/relatedTarget sequence, but an outside click must still run the
        // field evaluator exactly once.
        const handlePointerDown = (event: PointerEvent) => {
            const textarea = ref.current;
            if (!textarea || document.activeElement !== textarea || event.target === textarea) return;
            suppressNextBlurRef.current = true;
            setFocus(false);
            onBlur(value);
        };

        document.addEventListener("pointerdown", handlePointerDown, true);
        return () => document.removeEventListener("pointerdown", handlePointerDown, true);
    }, [onBlur, value]);

    const autosize = () => {
        const el = ref.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = Math.max(base, Math.min(el.scrollHeight + 2, maxH)) + "px";
    };

    useEffect(() => {
        if (focus) autosize();
        else if (ref.current) ref.current.style.height = base + "px";
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focus, value]);

    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--fg2)" }}>{label}</span>
                <ClearChip label={label} onClick={onClear} />
                {required && <RequiredBadge />}
                {badge}
                {maxLength !== undefined ? (
                    <span
                        style={{
                            fontSize: 10.5,
                            fontWeight: 600,
                            color: error ? "var(--error, #b42318)" : "var(--fg4)",
                            marginLeft: "auto"
                        }}
                    >
                        {value.length.toLocaleString()} / {maxLength.toLocaleString()}
                    </span>
                ) : focus ? (
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--np-brand)", marginLeft: "auto" }}>
                        expands as you type
                    </span>
                ) : null}
            </div>
            <textarea
                ref={ref}
                value={value}
                placeholder={placeholder}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? `${label.replace(/\s+/g, "-").toLowerCase()}-error` : undefined}
                onChange={e => onChange(e.target.value)}
                onFocus={() => {
                    suppressNextBlurRef.current = false;
                    setFocus(true);
                }}
                onBlur={() => {
                    setFocus(false);
                    if (suppressNextBlurRef.current) {
                        suppressNextBlurRef.current = false;
                        return;
                    }
                    onBlur?.(value);
                }}
                style={{
                    width: "100%",
                    display: "block",
                    resize: "none",
                    overflow: focus ? "auto" : "hidden",
                    height: base,
                    padding: "10px 13px",
                    borderRadius: 10,
                    border: error
                        ? "1.5px solid var(--error, #b42318)"
                        : focus
                          ? "1.5px solid var(--np-brand)"
                          : "1px solid var(--divider)",
                    outline: "none",
                    background: "var(--bg-paper)",
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    color: "var(--fg1)",
                    fontFamily: "inherit",
                    boxShadow: focus ? "0 4px 18px rgba(39,51,194,0.14)" : "var(--shadow-1)",
                    transition: "border-color .15s, box-shadow .15s"
                }}
            />
            {error && (
                <div
                    id={`${label.replace(/\s+/g, "-").toLowerCase()}-error`}
                    role="alert"
                    style={{ marginTop: 5, color: "var(--error, #b42318)", fontSize: 11.5 }}
                >
                    {error}
                </div>
            )}
        </div>
    );
}
